/**
 * Beeltra header wordmark decorative intro (v2).
 *
 * Homepage only (script is only <script>-tagged on request.page_type ==
 * 'index' in sections/header.liquid). Once per browser session
 * (sessionStorage) UNLESS ?beeltra_motion_test=1 is present in the URL,
 * which forces the sequence to run on every reload for QA -- it never
 * loops on its own within a single page view.
 *
 * Contract with the DOM (stable, not tied to CSS class names):
 *   [data-beeltra-wordmark]      -- wordmark visual container
 *   [data-beeltra-eeltra]        -- the "eeltra" text, direct child of the above
 *   .beeltra-eeltra-letter       -- the 6 individual letters inside it
 *   [data-beeltra-search-target] -- the search form to travel toward
 *
 * Every failure mode here must leave the wordmark fully visible and the
 * search input fully usable -- this script only ADDS transient decoration,
 * it never hides or moves the real letters/logo permanently. The
 * sessionStorage flag is only written AFTER we've confirmed every required
 * element exists AND the sequence has actually started -- never on a
 * missing-element abort.
 */
(function () {
  "use strict";

  var SESSION_KEY = "beeltraHeaderMotionShown";
  var DESKTOP_BREAKPOINT = 990; // matches the rest of this theme's own convention
  var TEST_MODE = new URLSearchParams(window.location.search).get("beeltra_motion_test") === "1";

  function warnMissing(selector) {
    // eslint-disable-next-line no-console
    console.warn("[beeltra-header-motion] required element not found, skipping decorative sequence: " + selector);
  }

  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (!TEST_MODE) {
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch (e) {
      // sessionStorage unavailable (private mode / disabled) -- fail safe by
      // simply not running the decorative sequence at all, rather than
      // risking it firing on every navigation.
      return;
    }
  }

  if (reduceMotion) {
    // Static, immediate, complete wordmark -- no drop, no travel, no hand.
    if (!TEST_MODE) {
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {}
    }
    return;
  }

  function init() {
    var wordmark = document.querySelector("[data-beeltra-wordmark]");
    if (!wordmark) return warnMissing("[data-beeltra-wordmark]");

    var eeltraContainer = wordmark.querySelector("[data-beeltra-eeltra]");
    if (!eeltraContainer) return warnMissing("[data-beeltra-eeltra]");

    var letters = eeltraContainer.querySelectorAll(".beeltra-eeltra-letter");
    if (!letters.length) return warnMissing(".beeltra-eeltra-letter (inside [data-beeltra-eeltra])");

    var searchForm = document.querySelector("[data-beeltra-search-target]");
    if (!searchForm) return warnMissing("[data-beeltra-search-target]");

    var searchInput = searchForm.querySelector("input[type='search']");
    if (!searchInput) return warnMissing("input[type='search'] (inside [data-beeltra-search-target])");

    // All required elements confirmed present -- safe to run.
    runSequence({
      letters: letters,
      searchForm: searchForm,
      searchInput: searchInput,
    });
  }

  function runSequence(refs) {
    var root = document.documentElement;
    var letters = refs.letters;
    var searchForm = refs.searchForm;
    var searchInput = refs.searchInput;

    var timers = [];
    var clones = [];
    var handEl = null;
    var cancelled = false;
    var started = false;
    var isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

    function markSessionShown() {
      if (TEST_MODE) return; // never persist in QA mode -- must repeat every reload
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {}
    }

    function clearTimers() {
      for (var i = 0; i < timers.length; i++) clearTimeout(timers[i]);
      timers.length = 0;
    }

    function removeClones() {
      for (var i = 0; i < clones.length; i++) {
        if (clones[i] && clones[i].parentNode) clones[i].parentNode.removeChild(clones[i]);
      }
      clones.length = 0;
    }

    function removeHand() {
      if (handEl && handEl.parentNode) handEl.parentNode.removeChild(handEl);
      handEl = null;
    }

    function removeGlow() {
      searchForm.classList.remove("beeltra-motion-glow");
    }

    function restoreLetters() {
      for (var i = 0; i < letters.length; i++) {
        letters[i].classList.remove("beeltra-letter-traveling");
      }
    }

    function cleanupAll() {
      clearTimers();
      removeClones();
      removeHand();
      removeGlow();
      restoreLetters();
      root.classList.remove("beeltra-motion-run");
    }

    function cancel() {
      if (cancelled) return;
      cancelled = true;
      root.classList.add("beeltra-motion-cancelled");
      cleanupAll();
      if (started) markSessionShown();
    }

    // Any of these means the user is actively using the search box (or the
    // page in general) -- stop the decorative sequence immediately and
    // leave everything fully functional.
    searchInput.addEventListener("focus", cancel, { once: true });
    searchInput.addEventListener("click", cancel, { once: true });
    searchInput.addEventListener("keydown", cancel, { once: true });
    document.addEventListener("click", cancel, { once: true });

    function buildHandSvg() {
      var svgNS = "http://www.w3.org/2000/svg";
      var svg = document.createElementNS(svgNS, "svg");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("width", "30");
      svg.setAttribute("height", "30");
      svg.setAttribute("fill", "none");
      svg.setAttribute("stroke", "#0b5fff");
      svg.setAttribute("stroke-width", "1.5");
      svg.setAttribute("stroke-linecap", "round");
      svg.setAttribute("stroke-linejoin", "round");
      var paths = [
        "M9 12.5V6a1.5 1.5 0 0 1 3 0v5",
        "M12 11V4.5a1.5 1.5 0 0 1 3 0V11",
        "M15 11.5V6a1.5 1.5 0 0 1 3 0v9",
        "M9 12.5c0-1-1.5-2-2.5-1S6 14 7 15l2.5 4.5c1 1.5 2.5 2 4 2h2c2.5 0 4-1.8 4-4.5v-4"
      ];
      for (var i = 0; i < paths.length; i++) {
        var p = document.createElementNS(svgNS, "path");
        p.setAttribute("d", paths[i]);
        svg.appendChild(p);
      }
      return svg;
    }

    function launchLetterTravel(onAllArrived) {
      if (cancelled) return;
      var searchRect = searchInput.getBoundingClientRect();
      var targetX = searchRect.left + searchRect.width * 0.5;
      var targetY = searchRect.top + searchRect.height * 0.5;
      var arrivedCount = 0;

      for (var i = 0; i < letters.length; i++) {
        (function (letterEl, index) {
          var launchT = setTimeout(function () {
            if (cancelled) return;
            var rect = letterEl.getBoundingClientRect();
            var clone = document.createElement("span");
            clone.className = "beeltra-motion-clone";
            clone.setAttribute("aria-hidden", "true");
            clone.textContent = letterEl.textContent;
            clone.style.fontSize = window.getComputedStyle(letterEl).fontSize;
            clone.style.transform = "translate(" + rect.left + "px, " + rect.top + "px)";
            clone.style.opacity = "1";
            document.body.appendChild(clone);
            clones.push(clone);

            // Hide the real letter the moment its copy starts traveling.
            letterEl.classList.add("beeltra-letter-traveling");

            // Force layout so the transition below is real, not collapsed
            // into the initial state.
            clone.getBoundingClientRect();

            requestAnimationFrame(function () {
              if (cancelled) return;
              clone.style.transform =
                "translate(" + targetX + "px, " + targetY + "px) scale(0.4)";
              clone.style.opacity = "0";
            });

            var arriveT = setTimeout(function () {
              if (clone.parentNode) clone.parentNode.removeChild(clone);
              var idx = clones.indexOf(clone);
              if (idx !== -1) clones.splice(idx, 1);
              arrivedCount++;
              if (arrivedCount === letters.length && !cancelled) {
                onAllArrived();
              }
            }, 550);
            timers.push(arriveT);
          }, index * 150);
          timers.push(launchT);
        })(letters[i], i);
      }
    }

    function showGlowAndHand() {
      if (cancelled) return;
      searchForm.classList.add("beeltra-motion-glow");

      var inputRect = searchInput.getBoundingClientRect();
      handEl = buildHandSvg();
      handEl.setAttribute("aria-hidden", "true");
      handEl.classList.add("beeltra-motion-hand");
      handEl.style.pointerEvents = "none";
      var handTop = inputRect.top - 34;
      var handLeft = inputRect.left + inputRect.width * 0.32;
      handEl.style.transform = "translate(" + handLeft + "px, " + handTop + "px)";
      document.body.appendChild(handEl);

      requestAnimationFrame(function () {
        if (handEl) handEl.classList.add("is-visible");
      });

      var t = setTimeout(function () {
        removeGlow();
        removeHand();
        cleanupAll();
      }, 1500);
      timers.push(t);
    }

    // --- Start ---
    root.classList.add("beeltra-motion-run");
    started = true;
    markSessionShown();

    if (!isDesktop) {
      // Mobile: only the drop (0 - ~1.05s). No travel, no hand, keep
      // header compact. Letters were never hidden, nothing to restore.
      var mobileDone = setTimeout(function () {
        root.classList.remove("beeltra-motion-run");
      }, 1200);
      timers.push(mobileDone);
      return;
    }

    // Desktop: drop (0 - ~1.05s) -> short pause -> travel (one by one,
    // each letter hidden the instant its copy launches) -> restore full
    // "eeltra" immediately once every copy has arrived -> glow + hand ->
    // cleanup.
    var travelStart = setTimeout(function () {
      launchLetterTravel(function () {
        restoreLetters();
        showGlowAndHand();
      });
    }, 1300);
    timers.push(travelStart);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
